import { Api } from '../api';
import { mapDataToInputItems } from '../filter/helpers';
import { SiftScreenEnum } from '../filter/model';
import { updateSiftByScreen } from '../filter/slice';
import {
  GetBeatPlanByRouteResponse,
  GetBeatPlanByRouteRequest,
  GetBeatPlanByTeamMemberResponse,
  GetBeatPlanByTeamMemberRequest,
  GetBeatPlanByFilterResponse,
  GetBeatPlanByFilterRequest,
  CreateBeatPlanByRouteResponse,
  CreateBeatPlanByRouteRequest,
  CreateBeatPlanByCustomerResponse,
  CreateBeatPlanByCustomerRequest,
  DeleteBeatPlanResponse,
  DeleteBeatPlanRequest,
  UpdateBeatPlanResponse,
  UpdateBeatPlanRequest,
  GetScheduleRequest,
  CustomerScheduleResponse,
  RouteScheduleResponse,
  GetBeatBlanByIdResponse,
  GetBeatBlanByIdRequest,
  GetBeatPlansResponse,
  GetBeatPlansRequest,
  GetBeatPlanCalenderResponse,
  GetBeatPlansFilterResponse,
  GetBeatPlansFilterRequest,
  GetBeatPlanDayViewResponse,
  GetBeatPlanDayViewRequest,
} from './model';

export const beatPlansApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getBeatPlanByRoute: builder.query<
      GetBeatPlanByRouteResponse,
      GetBeatPlanByRouteRequest
    >({
      query(arg) {
        const { routeId, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/calendar/route/${routeId}`,
          method: 'GET',
          params: params,
        };
      },
    }),

    getBeatPlanByTeamMember: builder.query<
      GetBeatPlanByTeamMemberResponse,
      GetBeatPlanByTeamMemberRequest
    >({
      query(arg) {
        const { teamMemberId, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/calendar/team-member/${teamMemberId}`,
          method: 'GET',
          params: params,
        };
      },
    }),

    getBeatPlanListView: builder.mutation<
      GetBeatPlansResponse,
      GetBeatPlansRequest
    >({
      query(arg) {
        const { body, ...otherPramas } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/schedule-list-view`,
          method: 'POST',
          params: otherPramas,
          body,
        };
      },
    }),

    getBeatPlanCalenderView: builder.mutation<
      GetBeatPlanCalenderResponse,
      GetBeatPlansRequest
    >({
      query(arg) {
        const { body, ...otherPramas } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/schedule-calendar-view`,
          method: 'POST',
          params: otherPramas,
          body,
        };
      },
    }),

    getBeatPlanDayView: builder.mutation<
      GetBeatPlanDayViewResponse,
      GetBeatPlanDayViewRequest
    >({
      query(arg) {
        const { sellerWorkspaceId, ...body } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/calendar/day-view`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body,
        };
      },
    }),

    getBeatPlansFilters: builder.mutation<
      GetBeatPlansFilterResponse,
      GetBeatPlansFilterRequest
    >({
      query(arg) {
        const { sellerWorkspaceId, ...body } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/calendar/filters`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body,
        };
      },

      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((response) => {
          const { data } = response;
          const customerGroupsArray =
            data?.customerGroups?.map((group) => ({
              id: group.customerGroupId,
              name: group.customerGroupName,
              eitherOr: true,
              selected: false,
            })) ?? [];
          const customersArray = data?.customers.map((customer) => ({
            id: customer.inviteId,
            name: customer.companyName,
            eitherOr: true,
            selected: false,
          }));
          const teamMembersArray = data?.teamMembers?.map((member) => ({
            id: member.teamMemberId,
            name: member.employeeName,
            eitherOr: true,
            selected: false,
          }));

          const customerGroupList = mapDataToInputItems(
            customerGroupsArray,
            'id',
            'name',
            'false'
          )?.filter((x: any) => x.value);

          const customersList = mapDataToInputItems(
            customersArray,
            'id',
            'name',
            'false'
          )?.filter((x: any) => x?.value);

          const teamMembersList = mapDataToInputItems(
            teamMembersArray,
            'id',
            'name',
            'false'
          )?.filter((x: any) => x?.value);

          dispatch(
            updateSiftByScreen({
              screen: SiftScreenEnum.BEAT_PLANS_SCREEN,
              filters: {
                customerGroups: customerGroupList,
                customers: customersList,
                teamMembers: teamMembersList,
              },
            })
          );
        });
      },
    }),

    getBeatPlanByFilter: builder.query<
      GetBeatPlanByFilterResponse,
      GetBeatPlanByFilterRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/beat-plan`,
          method: 'GET',
          params: arg,
        };
      },
    }),

    getBeatBlanById: builder.query<
      GetBeatBlanByIdResponse,
      GetBeatBlanByIdRequest
    >({
      query(arg) {
        const { beatPlanId, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/${beatPlanId}`,
          method: 'GET',
          params: params,
        };
      },
    }),

    createBeatPlanByRoute: builder.mutation<
      CreateBeatPlanByRouteResponse,
      CreateBeatPlanByRouteRequest
    >({
      query(arg) {
        const { body, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan`,
          method: 'POST',
          params: params,
          body: body,
        };
      },
    }),

    createBeatPlanByCustomer: builder.mutation<
      CreateBeatPlanByCustomerResponse,
      CreateBeatPlanByCustomerRequest
    >({
      query(arg) {
        const { body, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan`,
          method: 'POST',
          params: params,
          body: body,
        };
      },
    }),

    updateBeatPlan: builder.mutation<
      UpdateBeatPlanResponse,
      UpdateBeatPlanRequest
    >({
      query(arg) {
        const { beatPlanId, body, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/${beatPlanId}`,
          method: 'PUT',
          params: params,
          body: body,
        };
      },
    }),

    deleteBeatPlan: builder.mutation<
      DeleteBeatPlanResponse,
      DeleteBeatPlanRequest
    >({
      query(arg) {
        const { id, ...params } = arg;
        return {
          url: `/hub/orgs/api/beat-plan/${id}`,
          method: 'DELETE',
          params: params,
        };
      },
    }),
    getCustomerSchedule: builder.query<
      CustomerScheduleResponse,
      GetScheduleRequest
    >({
      query: (args) => ({
        url: `/hub/orgs/api/beat-plan/team/customers/${args.sellerWorkspaceId}`,
        method: 'GET',
        params: args,
        cache: 'no-cache',
      }),
    }),
    getRouteSchedule: builder.query<
      RouteScheduleResponse[],
      GetScheduleRequest
    >({
      query: (args) => ({
        url: `/hub/orgs/api/beat-plan/team/routes/${args.sellerWorkspaceId}`,
        method: 'GET',
        params: args,
        cache: 'no-cache',
      }),
    }),
  }),
});

export const {
  useCreateBeatPlanByCustomerMutation,
  useCreateBeatPlanByRouteMutation,
  useLazyGetBeatPlanByFilterQuery,
  useLazyGetBeatPlanByRouteQuery,
  useLazyGetBeatPlanByTeamMemberQuery,
  useDeleteBeatPlanMutation,
  useLazyGetCustomerScheduleQuery,
  useLazyGetRouteScheduleQuery,
  useUpdateBeatPlanMutation,
  useLazyGetBeatBlanByIdQuery,
  useGetBeatPlanCalenderViewMutation,
  useGetBeatPlanListViewMutation,
  useGetBeatPlanDayViewMutation,
  useGetBeatPlansFiltersMutation,
} = beatPlansApi;
